'use client'

import { useConvertNumber, useTranslate } from '@repo/shared/hooks'
import { FormSchema, useAppForm } from '@repo/ui/hooks'

import { useDepositOnlinePaymentSchema } from '@repo/shared/schemas'
import { useGetBanks, UserUserBankAccount } from '@repo/services'
import { convertToCardNumber } from '@repo/tools'
import {
  Card,
  Col,
  Alert,
  Form,
  FormField,
  FormItemWrapper,
  Icon,
  Row,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Typography,
} from '@repo/ui/components'
import { bankDetailsList } from '@repo/ui/components/bank-card-field/constants'
import { useIsDesktop } from '@repo/ui/hooks'
import { DepositWithNumberCTA } from './deposit-with-number.cta'
import { useEffect, useMemo } from 'react'
import { DepositOnlinePaymentAddCard } from '../online-payment/deposit-online-payment.add-card'
import { WithNumberOutputItem } from './with-number.output-item'

type Card = {
  enName: string | undefined
  card: string | undefined
  sheba: string | undefined
  default: boolean
  id: number
}

export const DepositWithNumberContainer = () => {
  const { t } = useTranslate()
  const { convertNumberToLetter } = useConvertNumber()
  const { data, isLoading } = useGetBanks({})

  const cards: Card[] = useMemo(
    () =>
      (data?.data?.bankAccounts || [])
        .filter((bank) => bank.type === 'card')
        .map((bank: UserUserBankAccount) => ({
          id: bank.id ?? 0,
          enName: bank.details?.bankName ?? 'Unknown',
          card:
            bank.type === 'card'
              ? convertToCardNumber(Number(bank.data))
              : undefined,
          sheba: bank.details?.iban,
          default: bank.default ?? false,
        })),
    [data?.data?.bankAccounts],
  )
  const defaultCard: Card | undefined = useMemo(
    () => cards.find((c) => c.default),
    [cards],
  )

  const { schema } = useDepositOnlinePaymentSchema()
  const { form } = useAppForm({
    formSchema: schema,
    defaultValues: {
      card: defaultCard ? (defaultCard.sheba ?? '') : undefined,
      amount: undefined,
    },
  })
  useEffect(() => {
    form.setValue('card', defaultCard ? (defaultCard.sheba ?? '') : undefined)
  }, [defaultCard, form])

  const onSubmit = (data: FormSchema<typeof schema>) => {}

  const isDesktop = useIsDesktop()

  const getBankIcon = (enName: string) => {
    const logo = bankDetailsList.find(
      (bank) =>
        bank.enNames.some(
          (name) => name.toLowerCase() === enName.toLowerCase(),
        ) || bank.name.toLowerCase() === enName.toLowerCase(),
    )
    return logo?.icon ?? <Icon name='CardLine' size='sm' />
  }

  const outputItems = [
    {
      title: t('depositWithNumberPage_select_sheba'),
      value: 'IR050560611828005150596801',
      isCopyable: true,
    },
    {
      title: t('depositWithNumberPage_deposit_id'),
      value: '1234567890',
      isCopyable: true,
      description: t('depositWithNumberPage_deposit_id_hint'),
    },
    {
      title: t('depositWithNumberPage_deposit_from'),
      value: t('depositWithNumberPage_deposit_from_value'),
      isCopyable: false,
    },
  ]

  return (
    <Card className='max-w-container-md mx-auto p-xl' withoutBorder>
      {isDesktop && <DepositWithNumberCTA origin='container' />}
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          id='deposit-with-number-form'
        >
          <Col className='gap-3xl'>
            <Alert
              color='alert'
              size='md'
              icon={<Icon name='AlertTriangleLine' size='sm' />}
              caption={t('depositWithNumberPage_hint')}
            />
            <FormField
              control={form.control}
              name='card'
              extra={<DepositOnlinePaymentAddCard />}
              render={({ field }) => (
                <Select {...field} onValueChange={field.onChange}>
                  <FormItemWrapper
                    title={t('depositWithNumberPage_select_card')}
                    inputSize='md'
                  >
                    <SelectTrigger inputSize='md' className='w-full'>
                      <SelectValue
                        placeholder={t(
                          'depositWithNumberPage_select_card_placeholder',
                        )}
                      />
                    </SelectTrigger>
                  </FormItemWrapper>
                  <SelectContent>
                    {cards.map((c, idx) => (
                      <SelectItem key={idx} value={c.sheba || String(idx)}>
                        <Row className='gap-xs items-center'>
                          {c.enName && getBankIcon(c.enName)}
                          <Typography size='md'>{c.sheba}</Typography>
                        </Row>
                      </SelectItem>
                    ))}
                    {cards.length === 0 && !isLoading && (
                      <Col className='min-h-20 text-center m-auto gap-md'>
                        <Typography
                          size='md'
                          className='m-auto'
                          color='text-neutral-surface-80'
                        >
                          {t('depositOnlinePaymentPage_select_card_empty')}
                        </Typography>
                      </Col>
                    )}
                  </SelectContent>
                </Select>
              )}
            />

            {outputItems.map((item) => (
              <WithNumberOutputItem key={item.title} {...item} />
            ))}

            <Alert
              color='base'
              size='md'
              title={t('depositWithNumberPage_estimated_fee', {
                fee: '0.01',
                amount: '100000000',
              })}
              hideIcon
              caption={t('depositWithNumberPage_estimated_fee_hint', {
                fee: convertNumberToLetter(0.01 * 100000000),
                amount: convertNumberToLetter(100000000),
              })}
            />
          </Col>
        </form>
      </Form>
    </Card>
  )
}
